import { memo, useEffect, useRef, useState } from 'react';
import { Focusable } from 'react-aria-components';

import { merge } from '@/components';
import { Text, TextProps, Tooltip, TooltipTrigger } from '@/components/core';

export type TruncatedTextProps = TextProps;

const ITruncatedText = ({ children, className, ...rest }: TruncatedTextProps) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const [isTextTruncated, setIsTextTruncated] = useState(false);

  useEffect(() => {
    const textElement = textRef.current;
    if (!textElement) return;

    const checkIfTruncated = () => {
      if (!textElement) return;
      setIsTextTruncated(textElement.scrollWidth > textElement.clientWidth);
    };

    checkIfTruncated();

    const resizeObserver = new ResizeObserver(checkIfTruncated);
    resizeObserver.observe(textElement);

    return () => {
      if (textElement) resizeObserver.unobserve(textElement);
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <TooltipTrigger isDisabled={!isTextTruncated}>
      <Tooltip variant="primary" placement="right">
        <Text slot="description">{children}</Text>
      </Tooltip>
      <Focusable>
        <Text role="button" ref={textRef} className={merge('truncate', className)} {...rest}>
          {children}
        </Text>
      </Focusable>
    </TooltipTrigger>
  );
};

export const TruncatedText = memo(ITruncatedText) as typeof ITruncatedText;
